/**
 * @file CustomerProfileTab.tsx
 * @description Вкладка «Профиль» карточки клиента (phase19.6; phase19.31 —
 * вынесено из страницы, минимум по SC50/SC63).
 *
 * Слева — разделы профиля как были (контакты, личный кабинет, юрлица,
 * адреса, согласия, блокировки); справа — «Служебное» и «Источник» в общих
 * карточках секций, подписи по-русски, без капса. Состав вкладок не
 * расширяется — это phase19.18.
 */

import { CustomerAccountSection } from "./CustomerAccountSection";
import { CustomerAddressesSection } from "./CustomerAddressesSection";
import { CustomerBlocksSection } from "./CustomerBlocksSection";
import { CustomerConsentsSection } from "./CustomerConsentsSection";
import { CustomerLegalEntitiesSection } from "./CustomerLegalEntitiesSection";
import { CustomerProfileSection } from "./CustomerProfileSection";
import { SectionCard } from "../../../../components/ui/SectionCard";
import type { CustomerDto } from "../../../../lib/api-customers";
import { dealSourceLabel } from "../../../../lib/crm-deal-view";
import { formatShopDate } from "../../../../lib/shop-time";

interface Props {
  customer: CustomerDto;
  canManage: boolean;
  onChanged: () => void;
}

/** Пара «подпись — значение» в боковой карточке. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="type-body text-on-surface-variant">{label}</dt>
      <dd className="type-body text-right font-medium text-on-surface">{value}</dd>
    </div>
  );
}

export function CustomerProfileTab({ customer, canManage, onChanged }: Props) {
  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12">
      <div className="flex flex-col gap-4 lg:col-span-8">
        <CustomerProfileSection customer={customer} />
        <CustomerAccountSection customerId={customer.id} canManage={canManage} />
        <CustomerLegalEntitiesSection customerId={customer.id} canManage={canManage} />
        <CustomerAddressesSection customerId={customer.id} canManage={canManage} />
        <CustomerConsentsSection customerId={customer.id} canManage={canManage} />
        <CustomerBlocksSection customerId={customer.id} canManage={canManage} onChanged={onChanged} />
      </div>
      <div className="flex flex-col gap-4 lg:col-span-4">
        <SectionCard title="Служебное">
          <dl className="flex flex-col gap-2">
            <Fact label="Создан" value={formatShopDate(customer.createdAt)} />
            <Fact label="Риск-скор" value={String(customer.riskScore)} />
          </dl>
        </SectionCard>
        {customer.acquisitionSource || customer.acquisitionErid ? (
          <SectionCard title="Источник">
            <dl className="flex flex-col gap-2">
              {customer.acquisitionSource ? (
                <Fact label="Канал" value={dealSourceLabel(customer.acquisitionSource) ?? customer.acquisitionSource} />
              ) : null}
              {customer.acquisitionErid ? <Fact label="Метка рекламы (ERID)" value={customer.acquisitionErid} /> : null}
            </dl>
          </SectionCard>
        ) : null}
      </div>
    </div>
  );
}
